import React from 'react';
import type { UnifiedCitation } from '../../types/citation';
import { CitationSuperscript } from './CitationSuperscript';
import { RedactionText } from '../redaction/RedactionText';

interface InlineCitedTextProps {
  /** Text that may contain [1], [2], etc. citation markers. */
  text: string;
  /** Citations array (1-indexed: [1] maps to citations[0]). */
  citations?: UnifiedCitation[];
}

/**
 * Inline-safe citation renderer for use inside buttons, spans, etc.
 * Replaces [N] markers with clickable CitationSuperscript components
 * without any block-level elements (unlike MarkdownWithCitations).
 */
export const InlineCitedText: React.FC<InlineCitedTextProps> = ({ text, citations }) => {
  if (!citations || citations.length === 0) {
    return <RedactionText text={text} />;
  }

  const regex = /\[(\d+)\]/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Track which citation numbers have been rendered so we don't repeat the same dot
  const renderedCitations = new Set<number>();

  while ((match = regex.exec(text)) !== null) {
    const num = parseInt(match[1], 10);
    const citIdx = num - 1;

    // Text before this marker
    if (match.index > lastIndex) {
      const gap = text.substring(lastIndex, match.index);
      // If there's meaningful text (not just whitespace), reset dedup tracking
      if (gap.trim().length > 0) {
        parts.push(<RedactionText key={`t-${lastIndex}`} text={gap} />);
        renderedCitations.clear();
      }
    }

    if (citIdx >= 0 && citIdx < citations.length && !renderedCitations.has(num)) {
      parts.push(<CitationSuperscript key={`c-${match.index}`} citation={citations[citIdx]} number={num} />);
      renderedCitations.add(num);
    }
    // Out-of-range or duplicate markers are silently dropped

    lastIndex = match.index + match[0].length;
  }

  // Trailing text
  if (lastIndex < text.length) {
    parts.push(<RedactionText key={`t-${lastIndex}`} text={text.substring(lastIndex)} />);
  }

  return parts.length === 1 ? <>{parts[0]}</> : <>{parts}</>;
};
